// @/utils/useGeoLocation.js
import { ref ,onBeforeUnmount} from 'vue'
/* eslint-disable */ 
export default function useGeolocation(options = {}) {
  const coords = ref(null)
  const locatedAt = ref(null)
  const error = ref(null)
  const isSupported = ref('geolocation' in navigator)
  const isTracking = ref(false)
  const watchId = ref(null)
  const lastGoodPosition = ref(null)

  // Enhanced default options
  const defaultOptions = {
    enableHighAccuracy: true,
    maximumAge: 10000, // Accept cached positions up to 10s old
    timeout: 10000, // Increased timeout
    distanceFilter: 5 // Only update if moved at least 5 meters
  }

  const mergedOptions = { ...defaultOptions, ...options }

  // Position caching
  const cachePosition = (position) => {
    lastGoodPosition.value = {
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading,
        speed: position.coords.speed
      },
      timestamp: position.timestamp
    }
  }

  // Error handling with fallbacks
  const handleError = (err) => {
    error.value = getError(err)
    
    // Fallback to last good position if available
    if (lastGoodPosition.value) {
      coords.value = lastGoodPosition.value.coords
      locatedAt.value = lastGoodPosition.value.timestamp
    }
  }

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!isSupported.value) {
        error.value = 'Geolocation not supported'
        return reject(new Error('Geolocation not supported'))
      }

    // console.log('Getting location...');

      // First try high accuracy
      navigator.geolocation.getCurrentPosition(
        position => {
          cachePosition(position)
          coords.value = position.coords
          locatedAt.value = position.timestamp
          error.value = null
          resolve(position)
        },
        highAccuracyError => {
          // Fallback to low accuracy if high fails
          navigator.geolocation.getCurrentPosition(
            position => {
              cachePosition(position)
              coords.value = position.coords
              locatedAt.value = position.timestamp
              error.value = null
              resolve(position)
            },
            lowAccuracyError => {
              handleError(lowAccuracyError)
              reject(lowAccuracyError)
            },
            { ...mergedOptions, enableHighAccuracy: false }
          )
        },
        { ...mergedOptions, enableHighAccuracy: true }
      )
    })
  }

  const startTracking = () => {
    if (!isSupported.value || isTracking.value) return
    
    watchId.value = navigator.geolocation.watchPosition(
      position => {
        cachePosition(position)
        coords.value = position.coords
        locatedAt.value = position.timestamp
        error.value = null
      },
      handleError,
      mergedOptions
    )
    
    isTracking.value = true
  }
  // Stop continuous position watching
  const stopTracking = () => {
    if (watchId.value) {
      navigator.geolocation.clearWatch(watchId.value)
      watchId.value = null
      isTracking.value = false
    }
  }

  // Get user-friendly error message
  const getError = (error) => {
    switch(error.code) {
      case error.PERMISSION_DENIED:
        return 'Location access was denied'
      case error.POSITION_UNAVAILABLE:
        return 'Location information is unavailable'
      case error.TIMEOUT:
        return 'Location request timed out'
      case error.UNKNOWN_ERROR:
        return 'An unknown error occurred'
      default:
        return 'Failed to get location'
    }
  }

  // Cleanup on unmount
  onBeforeUnmount(() => {
    stopTracking()
  })

  return {
    coords,
    locatedAt,
    error,
    isSupported,
    isTracking,
    getCurrentPosition,
    startTracking,
    stopTracking,
    lastGoodPosition
  }
}